<template>
  <DropdownTooltip
    v-if="projectName"
    placement="bottom-start"
    placementSub="right-start"
    :menu="options"
    :shown="selectedStep === step"
    :autoHide="selectedStep !== step"
    popperClassSub="sprint-selection header__breadcrumb-menu"
    :popperClass="`project-selection header__breadcrumb-menu ${selectedStep === step ? 'pointer-none' : ''}`"
    optionMenuClass="header__breadcrumb-menu-item"
    subMenuClass="header__breadcrumb-menu-item"
    submenuWaitTitle="projectTeams"
    @dropdownOptionMenu="onTeamClick"
  >
    <template #after>
      <TooltipLearn
        :step="step"
        :show="selectedStep === step"
        placement="right-bottom"
        :tooltipText="$t(`learnTooltipText.${step}`)"
        :distance="30"
        :skidding="-30"
      />
    </template>
    <template #tooltip-label>
      <BaseSubHeading
        size="large"
        weight="400"
        color="monochrome-05"
        className="header__breadcrumb"
      >
        /<span>{{ projectName }}</span>
      </BaseSubHeading>
    </template>
    <template #tooltip-item="{ item }">
      <DisabledFeatureTooltip
        v-if="item.disabledByPlan"
        :feature="item.feature"
      >
        <template #label>
          <span class="multiselect__option_team-name">{{ item.label }}</span>
        </template>
      </DisabledFeatureTooltip>
      <router-link
        v-else-if="item?.to"
        class="header__breadcrumb-menu-item_backlogs router-link-wrapper"
        :to="item.to"
      >
        <span class="multiselect__option_team-name">{{ item.label }}</span>
        <icon
          v-if="!item.hideOptions"
          width="6px"
          height="8px"
          data="@icon/triangle_right.svg"
          fill="false"
          :color="iconColor"
          class="team-selection__arrow"
        />
      </router-link>
      <div
        v-else
        class="header__breadcrumb-menu-item_backlogs"
      >
        <span class="multiselect__option_team-name">{{ item.label }}</span>
        <icon
          v-if="!item.hideOptions"
          width="6px"
          height="8px"
          data="@icon/triangle_right.svg"
          fill="false"
          :color="iconColor"
          class="team-selection__arrow"
        />
      </div>
    </template>
    <template #tooltip-submenu-item="{ option }">
      <router-link
        class="sprint-label router-link-wrapper"
        :to="{
          name: 'backlog',
          params: {
            projectId: selectedProject.urlName,
            teamId: option.urlName
          }
        }"
      >
        <span class="multiselect__option__avatar">
          <Avatar
            :src="option.picture?.pictureUrl"
            :size="optionLabelSize"
            type="teamsGroup"
            class="user__avatar"
          />
        </span>
        <span class="multiselect__option__name">{{ option.name }}</span>
      </router-link>
    </template>
  </DropdownTooltip>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component"
import { Emit, Prop } from "vue-property-decorator"
import { useAcl } from "vue-simple-acl"
import { Getter } from "s-vuex-class"

import DropdownTooltip from "@/components/Dropdown/DropdownTooltip.vue"
import TooltipLearn from "@/components/Dropdown/TooltipLearn.vue"
import DisabledFeatureTooltip from "@/components/Subscription/DisabledFeatureTooltip.vue"

import { Project, Team, TipsStepType, TypeOfSubscriptionFeatures, User } from "@/models"
import { UserSettings } from "@/models/User"
import { Getters } from "@/store"

import Avatar from "../Avatar/index.vue"

interface OptionMenu {
  label: string
  id: string
  options?: any[]
  hideOptions?: boolean
  to?: any
  disabledByPlan?: boolean
  feature?: TypeOfSubscriptionFeatures
}

@Options({
  name: "BreadcrumbsProjectsBlock",
  components: {
    DisabledFeatureTooltip,
    DropdownTooltip,
    Avatar,
    TooltipLearn
  }
})
export default class BreadcrumbsProjectsBlock extends Vue {
  @Prop({ required: true }) selectedProject: Project
  @Prop({ default: "sm" }) optionLabelSize: string
  @Getter(Getters.GET_USER_CURRENT_PROFILE) userProfile: User
  @Getter(Getters.GET_SELECTED_TIPS_STEP) readonly selectedStep: TipsStepType
  @Getter(Getters.GET_SESSION_PROJECT_ID) sessionProjectId: string
  @Getter(Getters.GET_PROJECT_TEAMS) projectTeams: Team[]
  @Getter(Getters.THEME_IS_DARK) isDark: boolean
  @Getter(Getters.GET_USER_CURRENT_PROFILE_SETTINGS) readonly userSettings: UserSettings

  step: TipsStepType = "breadcrumbs"

  get iconColor() {
    return this.isDark ? "#AFB4C0" : "#363945"
  }

  get options(): OptionMenu[] {
    const params = {
      projectId: this.selectedProject.urlName
    }
    const toBacklog =
      this.projectTeams.length === 1
        ? {
            name: "backlog",
            params: {
              ...params,
              teamId: this.projectTeams[0].urlName
            }
          }
        : false
    let allOptions: OptionMenu[] = [
      {
        label: "Roadmap & Goals",
        id: "roadmap",
        hideOptions: true,
        to: {
          name: "roadmap",
          params: {
            projectId: this.selectedProject.urlName
          }
        }
      },
      {
        label: "Project",
        id: "project",
        hideOptions: true,
        to: {
          name: "project",
          params,
          query: {
            back: "true"
          }
        }
      },
      {
        label: "Milestones",
        id: "milestones",
        hideOptions: true,
        to: {
          name: "Milestones",
          params
        },
        disabledByPlan: !useAcl().can("hasSubscription"),
        feature: "productMilestones"
      },
      {
        label: "Product Backlog",
        id: "productBacklog",
        hideOptions: true,
        to: {
          name: "product-backlog",
          params
        }
      },
      {
        label: "Epics",
        id: "epics",
        hideOptions: true,
        to: {
          name: "epics",
          params
        }
      },
      {
        label: "Sprints",
        id: "sprints",
        hideOptions: true,
        to: {
          name: "sprints",
          params
        }
      }
    ]
    if (this.projectTeams.length > 0) {
      allOptions.push({
        label: "Team Backlogs",
        id: "backlogs",
        options: this.projectTeams,
        hideOptions: this.projectTeams.length === 1,
        to: toBacklog
      })
    }
    return allOptions
  }

  get projectName() {
    return this.selectedProject?.name ?? ""
  }

  @Emit()
  onTeamClick({ value, hide }) {
    hide()
    return value
  }
}
</script>
